import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { ModuleRegistry } from "@ag-grid-community/core";
import customer_table1 from "../../assets/images/admin/customer_table1.svg";
import { apiUrl } from "../../config/Config";

ModuleRegistry.registerModules([ClientSideRowModelModule]);

const CustomerDetails = () => {
  const navigate = useNavigate();
  const { customerId } = useParams();
  const userdata = useSelector((store) => store.userReducer.userdata);
  const [orderStatus, setOrderStatus] = useState("allorders");
  const [customerDetails, setCustomerDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const paginationPageSizeSelector = useMemo(() => [10, 20, 50, 100], []);

  const headers = {
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + userdata?.token,
    },
  };

  const formatCurrency = (amount) => `₹ ${Number(amount || 0).toFixed(2)}`;

  const formatDate = (dateValue) => {
    if (!dateValue) return "NA";
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return dateValue;
    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  const formatAddress = (address) => {
    if (!address) return "No address added";
    return [
      address.name,
      address.street,
      address.city,
      address.state,
      address.zip,
      address.country,
    ].filter(Boolean).join(", ");
  };

  const statusClass = (status) => {
    if (["paid", "completed", "delivered"].includes(status)) return "completed";
    if (["failed", "cancelled", "canceled", "payment_failed"].includes(status)) return "cancelled";
    return "processing";
  };

  const fetchCustomerDetails = async () => {
    if (!customerId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    await axios
      .post(apiUrl + "getadmincustomerdetails", { customer_id: customerId }, headers)
      .then((res) => {
        if (res.data.status === "success") {
          setCustomerDetails(res.data.data);
        } else {
          Swal.fire({
            text: res.data.message,
            icon: "error",
            confirmButtonColor: "rgb(59, 151, 196)",
          });
        }
      })
      .catch((err) => {
        Swal.fire({
          text: err.message,
          icon: "error",
          confirmButtonColor: "rgb(59, 151, 196)",
        });
      });
    setLoading(false);
  };

  useEffect(() => {
    if (userdata && Object.keys(userdata).length > 0) {
      fetchCustomerDetails();
    } else {
      navigate("/admin/login");
    }
  }, [customerId]);

  const orders = customerDetails?.orders || [];
  const rowData = useMemo(() => {
    const mappedOrders = orders.map((order) => ({
      orderID: order.order_number || `Order #${order.id}`,
      product: `${order.items_count || 0} item(s)`,
      date: formatDate(order.created_at),
      status: order.payment_status || order.status || "pending",
      totalPrice: formatCurrency(order.amount),
    }));

    if (orderStatus === "allorders") return mappedOrders;
    if (orderStatus === "completed") {
      return mappedOrders.filter((order) => ["paid", "completed", "delivered"].includes(order.status));
    }
    return mappedOrders.filter((order) => ["failed", "cancelled", "canceled", "payment_failed"].includes(order.status));
  }, [orders, orderStatus]);

  const colDefs = useMemo(
    () => [
      { field: "orderID", headerName: "Order ID", filter: true },
      { field: "product", headerName: "Items" },
      { field: "date", headerName: "Date" },
      { field: "totalPrice", headerName: "Total" },
      {
        field: "status",
        headerName: "Status",
        cellRenderer: (props) => (
          <div className={`orderstatusbg ${statusClass(props.data.status)}`}>
            <p>{props.data.status}</p>
          </div>
        ),
      },
    ],
    []
  );

  if (!customerId) {
    return (
      <div className="dashboardbox" style={{ width: "100%" }}>
        <p className="dashboardboxsubtitle">Please open a customer from Customers, Orders, Reviews, Questions or Refunds.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="dashboardbox" style={{ width: "100%" }}>
        <p className="dashboardboxsubtitle">Loading customer details...</p>
      </div>
    );
  }

  const customer = customerDetails?.customer || {};
  const stats = customerDetails?.stats || {};

  return (
    <div>
      <div className="dashboardbox" style={{ width: "100%" }}>
        <div className="customerdetailsrow row">
          <div className="col-md-4">
            <div className="customerdetailsbox">
              <div className="customerdetailsnamediv">
                <img src={customer_table1} className="customerdetailsicon" alt="" />
                <div className="customerdetailsnamerightdiv">
                  <p className="customerdetailsnamerightdiv_name">
                    {customer.fullname || "Customer"}
                  </p>
                  <p className="customerdetailsnamerightdiv_email">
                    {customer.email || "No email"}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="customerdetailsbox">
              <p className="personal-info-title">PERSONAL INFORMATION</p>
              <div className="personal-info-row">
                <p className="personal-info-key">Contact Number</p>
                <p className="personal-info-value">{customer.number || "NA"}</p>
              </div>
              <div className="personal-info-row">
                <p className="personal-info-key">Status</p>
                <p className="personal-info-value">{customer.status || "NA"}</p>
              </div>
              <div className="personal-info-row">
                <p className="personal-info-key">Member Since</p>
                <p className="personal-info-value">{formatDate(customer.created_at)}</p>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <p className="personal-info-title">SHIPPING ADDRESS</p>
            <p className="personal-info-titledesc">{formatAddress(customer.shippingaddress)}</p>
            <div className="customerdetails_ordersrow">
              <div>
                <p className="customerdetails_ordernumber">{stats.total_orders || 0}</p>
                <p className="customerdetails_ordertext">Total Order</p>
              </div>
              <div>
                <p className="customerdetails_ordernumber">{stats.completed_orders || 0}</p>
                <p className="customerdetails_ordertext">Completed</p>
              </div>
              <div>
                <p className="customerdetails_ordernumber">{stats.cancelled_orders || 0}</p>
                <p className="customerdetails_ordertext">Canceled</p>
              </div>
            </div>
          </div>
        </div>

        <div className="customerdetails_ordersrow mt-4">
          <div>
            <p className="customerdetails_ordernumber">{stats.questions_count || 0}</p>
            <p className="customerdetails_ordertext">Questions</p>
          </div>
          <div>
            <p className="customerdetails_ordernumber">{stats.refunds_count || 0}</p>
            <p className="customerdetails_ordertext">Refunds</p>
          </div>
          <div>
            <p className="customerdetails_ordernumber">{formatCurrency(stats.total_spent)}</p>
            <p className="customerdetails_ordertext">Total Spent</p>
          </div>
        </div>

        <div className="productstogglebar mt-5">
          <div className="orderstatustogglediv">
            {[
              ["allorders", "All Orders"],
              ["completed", "Completed"],
              ["cancelled", "Cancelled"],
            ].map(([key, label]) => (
              <div
                key={key}
                className={orderStatus === key ? "orderstatusdiv active" : "orderstatusdiv"}
                onClick={() => setOrderStatus(key)}
              >
                <p>{label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="ag-theme-quartz mt-4" style={{ width: "100%", height: 550 }}>
          <AgGridReact
            rowData={rowData}
            columnDefs={colDefs}
            defaultColDef={{ flex: 1 }}
            suppressRowClickSelection={true}
            groupSelectsChildren={true}
            rowSelection={"multiple"}
            pagination={true}
            paginationPageSize={10}
            paginationPageSizeSelector={paginationPageSizeSelector}
            rowHeight={50}
          />
        </div>
      </div>
    </div>
  );
};

export default CustomerDetails;
